import { Popover, PopoverContent, PopoverTrigger } from '@heroui/react';
import { ChevronsUpDown } from 'lucide-react';
import { FC, useEffect, useState } from 'react';
import Avatar from 'react-avatar';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useTheme } from 'styled-components';

import { usePermissions } from '@/hooks/usePermissions';
import { useOrganizationState, useUserState } from '@/state';
import { useLocaleNavigate } from '@/utils/locale';
import { isOrganizationOwner } from '@/utils/organizationOwner';

import {
  AvatarBox,
  AvatarChevron,
  CompactUserInfo,
  Divider,
  DropdownContainer,
  HeaderAvatarBox,
  MenuItem,
  MenuItemLabel,
  MenuSection,
  StaffDot,
  UsageRing,
  UsageRingLabel,
  UserEmail,
  UserInfoSection,
  UserMenuTrigger,
  UserName,
} from './UserMenu.styled';
import { UserMenuProps } from './UserMenu.types';

export const UserMenu: FC<UserMenuProps> = ({
  userName,
  userEmail,
  userAvatar,
  onProfileClick,
  onLogout,
  showMyProfile = true,
  avatarVariant = 'default',
  aiRemainingPercent,
  customMenuItems,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { isStaff, ownerClaim } = usePermissions();
  const { owner, organizationId } = useOrganizationState();
  const { currentUser } = useUserState();
  // Właściciel AKTUALNEJ organizacji (#81): `organizations/{id}.owner` albo claim
  // `owner` z jej id. Globalna rola `owner` nie wystarcza.
  const isOwner = isOrganizationOwner({
    uid: currentUser?.uid,
    organizationId,
    organizationOwner: owner,
    ownerClaim,
  });
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const isLight = avatarVariant === 'light';
  // react-avatar liczy inicjały (i pokazuje zdjęcie z `src`, gdy dostępne)
  // z `name`. Gdy brak imienia, używamy emaila jako fallbacku.
  const avatarName = userName?.trim() || userEmail?.trim() || 'U';

  const handleMenuItemClick = (callback?: () => void) => {
    callback?.();
    setIsOpen(false);
  };

  const navigate = useLocaleNavigate();

  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  // Pierścień zużycia AI: łuk = pozostały procent limitu; poniżej 20% żółty, 0 czerwony.
  const ringPercent = typeof aiRemainingPercent === 'number' && Number.isFinite(aiRemainingPercent)
    ? Math.max(0, Math.min(100, aiRemainingPercent))
    : null;
  const RING_RADIUS = 19;
  const ringCircumference = 2 * Math.PI * RING_RADIUS;
  const ringColor = (() => {
    if (ringPercent === null) return 'transparent';
    if (ringPercent <= 0) return '#dc2626';
    if (ringPercent < 20) return '#d97706';
    if (isLight) return '#000000';
    return theme.mode === 'dark' ? '#f3f4f6' : '#111827';
  })();
  const ringTitle = ringPercent === null
    ? undefined
    : t('topbar.aiRemaining', 'Pozostało {{percent}}% limitu AI w tym miesiącu', { percent: Math.round(ringPercent) });

  return (
    <Popover
      placement="bottom-start"
      showArrow={false}
      offset={10}
      isOpen={isOpen}
      onOpenChange={setIsOpen}
      shadow="none"
      disableAnimation
    >
      <PopoverTrigger>
        <UserMenuTrigger
          aria-label={t('userMenu.userMenu')}
          onClick={() => setIsOpen(!isOpen)}
        >
          <AvatarBox $variant={avatarVariant} $hasRing={ringPercent !== null} title={ringTitle}>
            {ringPercent !== null && (
              <UsageRing viewBox="0 0 42 42" aria-hidden="true">
                <circle className="track" cx="21" cy="21" r={RING_RADIUS} />
                <circle
                  className="value"
                  cx="21"
                  cy="21"
                  r={RING_RADIUS}
                  stroke={ringColor}
                  strokeDasharray={ringCircumference}
                  strokeDashoffset={ringCircumference * (1 - ringPercent / 100)}
                />
              </UsageRing>
            )}
            <Avatar
              name={avatarName}
              src={userAvatar || undefined}
              size="34"
              round
              maxInitials={2}
              textSizeRatio={2.2}
              color={isLight ? '#ffffff' : '#f3f4f6'}
              fgColor={isLight ? '#000000' : '#374151'}
            />
            {/* Kropka statusu: niebieska dla właściciela organizacji, zielona dla
                pozostałego personelu (claim isStaff). */}
            {ringPercent !== null && (
              <UsageRingLabel aria-hidden="true">{`${Math.round(ringPercent)}%`}</UsageRingLabel>
            )}
            {isStaff && (
              <StaffDot
                $owner={isOwner}
                aria-label={isOwner
                  ? t('topbar.ownerStatus', 'Właściciel')
                  : t('topbar.staffStatus', 'Personel')}
                title={isOwner
                  ? t('topbar.ownerStatus', 'Właściciel')
                  : t('topbar.staffStatus', 'Personel')}
              />
            )}
          </AvatarBox>
          <AvatarChevron>
            <ChevronsUpDown size={14} strokeWidth={2} />
          </AvatarChevron>
        </UserMenuTrigger>
      </PopoverTrigger>

      <PopoverContent className="p-0 border-none shadow-none">
        <DropdownContainer>
          {/* Kompaktowy header — awatar + imię/email w jednej linii */}
          <UserInfoSection>
            <HeaderAvatarBox>
              <Avatar
                name={avatarName}
                src={userAvatar || undefined}
                size="32"
                round
                maxInitials={2}
                textSizeRatio={2.3}
                color={theme.mode === 'dark' ? '#374151' : '#f3f4f6'}
                fgColor={theme.mode === 'dark' ? '#f3f4f6' : '#374151'}
              />
            </HeaderAvatarBox>
            <CompactUserInfo>
              {userName && <UserName title={userName}>{userName}</UserName>}
              {userEmail && <UserEmail title={userEmail}>{userEmail}</UserEmail>}
            </CompactUserInfo>
          </UserInfoSection>

          <MenuSection>
            {showMyProfile && onProfileClick && (
              <MenuItem onClick={() => handleMenuItemClick(onProfileClick)}>
                <MenuItemLabel>{t('userMenu.myProfile', 'Mój profil')}</MenuItemLabel>
              </MenuItem>
            )}

            <MenuItem
              onClick={() => {
                navigate('/support-service');
                setIsOpen(false);
              }}
            >
              <MenuItemLabel>{t('userMenu.support', 'Wsparcie')}</MenuItemLabel>
            </MenuItem>

            {customMenuItems?.map((item) => (
              <MenuItem key={item.key} $danger={item.danger} onClick={() => handleMenuItemClick(item.onClick)}>
                {item.icon}
                <MenuItemLabel>{item.label}</MenuItemLabel>
              </MenuItem>
            ))}
          </MenuSection>

          {onLogout && (
            <>
              <Divider />
              <MenuSection>
                <MenuItem $danger onClick={() => handleMenuItemClick(onLogout)}>
                  <MenuItemLabel>{t('userMenu.logout', 'Wyloguj')}</MenuItemLabel>
                </MenuItem>
              </MenuSection>
            </>
          )}

          <Divider />
          <MenuSection>
            <MenuItem
              onClick={() => {
                navigate('/staff-app');
                setIsOpen(false);
              }}
            >
              <MenuItemLabel>{t('staffApp.sidebar.title', 'Aplikacja dla zespołu')}</MenuItemLabel>
            </MenuItem>
          </MenuSection>
        </DropdownContainer>
      </PopoverContent>
    </Popover>
  );
};
